import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { CoreDataService } from '@app/core/async-services/http/core.data';
import { VersionConfigService } from '@app/core/services/version-config/version-config.service';
import {
  HotelGroup,
  HotelType,
} from '@app/core/async-services/http/versioned/interfaces';
import { CustomEncoder } from '../http.util';
import { Platform } from '@ionic/angular';
import { API, User } from '@app/core/models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UsersHttpService {
  private _versionBasedUrlPrefix = '';

  constructor(
    private _coreDataService: CoreDataService,
    private _platform: Platform,
    private _versionConfigService: VersionConfigService,
  ) {
    this._versionConfigService.getVersion('users').subscribe((prefix) => {
      this._versionBasedUrlPrefix = `${prefix}/users/`;
    });
  }

  getCurrentLoggedInUserInformation() {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
    };

    return this._coreDataService.get<API.GetCurrentLoggedInUserInfoSuccess>(
      `${this._versionBasedUrlPrefix}current`,
      options,
    );
  }

  getOvertimeRiskHours(entId: number) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
      params: null,
    };

    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: entId.toString(),
      },
    );

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}overtime/hours`,
      options,
    );
  }

  getAbsenceMonitorData(entId: number) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
      params: null,
    };

    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: entId.toString(),
      },
    );

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}absence-monitor/data`,
      options,
    );
  }

  getOvertimeRiskSpent(entId: number) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
      params: null,
    };

    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: entId.toString(),
      },
    );

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}overtime/spent`,
      options,
    );
  }

  getRevenueYesterday(entId: number) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
      params: null,
    };

    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: entId.toString(),
      },
    );

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}revenue/yesterday`,
      options,
    );
  }

  getRevenueMonthToDate(entId: number) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
      params: null,
    };

    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: entId.toString(),
      },
    );

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}revenue/mtd`,
      options,
    );
  }

  getLaborWinLossHotelGroupContainer(groupId: string) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
      params: null,
    };

    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        groupid: groupId,
      },
    );

    return this._coreDataService.get<HotelGroup.WinLossResponse>(
      `${this._versionBasedUrlPrefix}labor/hotel_group/winloss`,
      options,
    );
  }

  getLaborWinLossContainer(entId: string) {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
      params: null,
    };

    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: entId.toString(),
      },
    );

    return this._coreDataService.get<any>(
      `${this._versionBasedUrlPrefix}labor/hotel/winloss`,
      options,
    );
  }

  getCommunicationsPreferences() {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};

    return this._coreDataService.get<API.GetNotificationPreferencesSuccess>(
      `${this._versionBasedUrlPrefix}notification-preferences`,
      options,
    );
  }

  updateNotificationPreference(
    preference: Partial<User.NotificationPreferences>,
  ) {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};

    const body = { preference };

    return this._coreDataService.post<API.Response>(
      `${this._versionBasedUrlPrefix}notification-preferences`,
      body,
      options,
    );
  }

  getHotelsByService(serviceIds: string[]): Observable<HotelType[]> {
    const options = this._coreDataService.getHttpOptions({ serviceIds });
    return this._coreDataService
      .get<API.Response<{ hotels: HotelType[] }>>(
        `${this._versionBasedUrlPrefix}hotels-by-service`,
        options,
      )
      .pipe(map((response) => response.payload?.hotels as HotelType[]));
  }
}
